import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useCalendly from '../hooks/useCalendly';
import SuccessModal from './SuccessModal';

/**
 * Reusable Calendly Button Component
 * @param {string} calendlyUrl - Your Calendly scheduling URL
 * @param {string} buttonText - Text to display on button (default: "Book Appointment")
 * @param {string} className - Additional CSS classes for styling
 * @param {Object} calendlyOptions - Additional Calendly options (prefill, utm, etc.)
 * @param {React.ReactNode} icon - Optional icon to display before button text
 * @param {Function} onSuccess - Optional callback fired after successful booking and modal close
 * @param {string} appointmentType - Type of appointment (e.g., "Design Discussion", "Fitting Session")
 */
const CalendlyButton = ({
    calendlyUrl,
    buttonText = "Book Appointment",
    className = "",
    calendlyOptions = {},
    icon = null,
    onSuccess = null,
    appointmentType = "Appointment"
}) => {
    const navigate = useNavigate();

    // Local state for success modal
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [bookingData, setBookingData] = useState(null);

    // Handle successful booking - show modal on same page
    const handleEventScheduled = (payload) => {
        setBookingData(payload);
        setShowSuccessModal(true);
    };

    const { isCalendlyLoaded, openCalendly } = useCalendly(handleEventScheduled);

    const handleClick = () => {
        if (!calendlyUrl) {
            console.error('Calendly URL is required');
            return;
        }
        openCalendly(calendlyUrl, calendlyOptions);
    };

    const closeSuccessModal = () => {
        setShowSuccessModal(false);
        setBookingData(null);

        // Call onSuccess callback if provided (e.g., to reset parent state)
        if (onSuccess) {
            onSuccess();
        }

        // Navigate to home and scroll to top
        navigate('/home', { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <button
                onClick={handleClick}
                disabled={!isCalendlyLoaded}
                className={className}
                type="button"
            >
                {icon && icon}
                {isCalendlyLoaded ? buttonText : 'Loading...'}
            </button>

            <SuccessModal
                isOpen={showSuccessModal}
                onClose={closeSuccessModal}
                bookingData={bookingData}
                appointmentType={appointmentType}
            />
        </>
    );
};

export default CalendlyButton;
